import { useUserStore } from '@/store/user';
import QRCode from 'qrcode';
import dayjs from 'dayjs';
import { useRecoveryStore } from '@/store/recovery';
import { useChainAccountStore } from '@/store/chain-account';
import { upError, upSuccess, upInfo } from '@/utils/useUniPass';
import { ADDRESS_ZERO } from '@/service/constants';
import { checkUpSignTokenForCancelRecovery } from '@/utils/oauth/check-up-sign-token';
import jsQR from 'jsqr';
import { useWalletConnectStore } from '@/store/wallet-connect';
import { getChainName, getChainNameByChainId } from '@/service/chains-config';
import { formatImgUrl } from '@/utils/formatter';
import { useCoinStore } from '@/store/coin';
import { useSignStore } from '@/store/sign';
import { LocalStorageService } from '@/store/storages';
export const useIndex = () => {
  const isDark = useDark();
  const userStore = useUserStore();
  const showReceive = ref(false);
  const showAddToken = ref(false);
  const showAddCustomToken = ref(false);
  const chainAccountStore = useChainAccountStore();
  onBeforeMount(() => {
    LocalStorageService.remove('OAUTH_REDIRECT');
  });
  return {
    showReceive,
    showAddCustomToken,
    showAddToken,
    userStore,
    formatImgUrl,
    ADDRESS_ZERO,
    isDark,
    chainAccountStore
  };
}; // Coin

export const useIndexCoin = () => {
  // import
  const router = useRouter();
  const coinStore = useCoinStore();

  const sendCoin = i => {
    const coin = coinStore.coins[i];
    router.push({
      path: '/send',
      query: {
        symbol: coin.symbol,
        chain: coin.chain,
        contractAddress: coin.contractAddress
      }
    });
  };

  const toBuyCoins = () => {
    const net = process.env.VUE_APP_Net;

    if (net === 'mainnet' || net === 'preview') {
      router.push('/buy-coins');
    } else {
      upInfo('testnet is not supported');
    }
  };

  return {
    sendCoin,
    toBuyCoins
  };
}; // Recovery

export const useIndexRecovery = () => {
  const recoveryStore = useRecoveryStore();
  const userStore = useUserStore();
  const chainAccountStore = useChainAccountStore();
  const recovering = reactive({
    loading: false
  });
  let polling;
  const auth = reactive({
    show: false,
    password: ''
  });

  const cancelRecovery = async () => {
    recovering.loading = true;

    try {
      const ok = await recoveryStore.sendCancelRecovery(auth.password);

      if (ok) {
        const date = dayjs().add(2, 'minute');
        polling = setInterval(async () => {
          await chainAccountStore.fetchAccountInfo(userStore.accountInfo.address, true, userStore.accountInfo.keyset.hash);

          if (!chainAccountStore.isPending) {
            clearInterval(polling); // @TODO change to i18n

            upSuccess('Cancel recovery success');
            recovering.loading = false;
            auth.show = false;
          }

          if (date.isBefore(dayjs())) {
            clearInterval(polling);
            console.error('Cancel recovery timeout');
            recovering.loading = false;
            auth.show = false;
          }
        }, 4000);
      }
    } catch (error) {
      recovering.loading = false;
      upError((error === null || error === void 0 ? void 0 : error.message) || 'unknown error');
    }
  };

  const beforeCancelRecovery = async () => {
    const needOAuth = await checkUpSignTokenForCancelRecovery();
    if (!needOAuth) auth.show = true;
  };

  return {
    recovering,
    cancelRecovery,
    beforeCancelRecovery,
    auth
  };
}; // WalletConnect

export const useIndexWalletConnect = () => {
  const {
    t: $t
  } = useI18n();
  const walletConnectStore = useWalletConnectStore();
  const userStore = useUserStore();
  const cameraElement = ref();
  const scan = reactive({
    isScanning: false,
    frameId: 0,
    cameraAll: 0,
    cameraCount: 0,
    showQuick: false,
    showMigration: false,
    addressQRCode: ''
  });
  const scanModal = {
    open: async () => {
      if (scan.isScanning) {
        return;
      }

      scan.isScanning = true;
      scan.cameraAll = 0;
      scan.cameraCount = 0;

      try {
        const stream = await window.navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment'
          },
          audio: false
        });

        if (!stream) {
          upError('no source found');
          return;
        }

        const camera = cameraElement.value;
        if (!camera) return;
        const canvas = document.createElement('canvas');
        const requestAnimationFrame = window.requestAnimationFrame || window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || window.msRequestAnimationFrame;
        camera.srcObject = stream;
        camera.play();

        const tick = () => {
          if (!scan.isScanning) {
            scanModal.close();
            return;
          }

          if (camera.readyState === camera.HAVE_ENOUGH_DATA && canvas) {
            canvas.width = camera.videoWidth;
            canvas.height = camera.videoHeight;
            const ctx = canvas.getContext('2d');

            if (ctx) {
              const rect = [0, 0, canvas.width, canvas.height];
              ctx.drawImage(camera, 0, 0);
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
              const code = jsQR(imageData.data, rect[2], rect[3], {
                inversionAttempts: 'dontInvert'
              });

              if (code && code.data) {
                scanModal.close();
                walletConnectStore.pair(code.data);
              }
            }
          }

          scan.frameId = requestAnimationFrame(tick);
        };

        scan.frameId = requestAnimationFrame(tick);
      } catch (err) {
        upError($t('NoCameraPermission'));
      }
    },
    close: () => {
      scan.isScanning = false;
      const camera = cameraElement.value;
      if (!camera) return;
      camera.pause();

      if (scan.frameId) {
        const cancelAnimationFrame = window.cancelAnimationFrame || window.mozCancelAnimationFrame;
        cancelAnimationFrame(scan.frameId);
        scan.frameId = 0;
      }

      const mediaStream = camera.srcObject;

      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }

      camera.srcObject = null;
    },
    pasteCode: async () => {
      try {
        const clipText = await navigator.clipboard.readText();
        await walletConnectStore.pair(clipText);
      } catch (e) {
        upError($t('NoClipboardPermission'));
      } finally {
        scanModal.close();
      }
    },
    initQRCode: block => {
      // https://www.npmjs.com/package/qrcode#example-1
      QRCode.toDataURL(block, {
        type: 'image/png',
        width: 160,
        margin: 0,
        // errorCorrectionLevel: 'L',
        color: {
          dark: '#000000',
          light: '#FFFFFF00'
        }
      }).then(data => {
        scan.addressQRCode = data;
      });
    }
  };
  const chainName = computed(() => getChainName(getChainNameByChainId(userStore.chainId)));
  onBeforeMount(() => {
    walletConnectStore.init();
  });
  onBeforeUnmount(scanModal.close);
  return {
    chainName,
    walletConnectStore,
    scan,
    scanModal,
    cameraElement
  };
}; // for special third-party

export const useIndexPageStyle = () => {
  const signStore = useSignStore();
  const isDeheroEnv = computed(() => {
    return signStore.appName === 'deherotest' || signStore.appName === 'deheropre';
  });
  return {
    isDeheroEnv
  };
};